import { createRootRoute, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { styled } from '@mui/material/styles';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useMediaQuery } from '@mui/material';
import { indigo, pink } from '@mui/material/colors';

import Snackbar from '~/containers/Snackbar';
import DrawerMenu from '~/containers/DrawerMenu';
import ConfigureFlatpickrLocale from '~/containers/ConfigureFlatpickrLocale';
import PageTitle from '~/containers/PageTitle';
import PostHogConsentManager from '~/containers/PostHogConsentManager';
import CookieConsentBanner from '~/containers/CookieConsentBanner';

import { deDE, enUS, esES, frFR, nbNO, ruRU } from '@mui/material/locale';
import { useUILanguage } from '~/containers/UILanguage';

const PREFIX = 'App';

const classes = {
  drawer: `${PREFIX}-drawer`,
};

const Root = styled('div')({
  height: '100%',
  [`& .${classes.drawer}`]: {
    width: 300,
  },
});

const Container = styled('div')({
  display: 'flex',
  height: '100%',
});

const Content = styled('div')({
  flex: 1,
  display: 'flex',
  minWidth: 0,
  '& > *': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
});

const muiLocaleMapping = {
  en: enUS,
  de: deDE,
  ru: ruRU,
  es: esES,
  fr: frFR,
  nb: nbNO,
};

const RootLayout = () => {
  const { locale } = useUILanguage();
  const theme = createTheme(
    {
      palette: {
        primary: {
          main: indigo[500],
        },
        secondary: {
          main: pink[500],
        },
      },
    },
    muiLocaleMapping[locale]
  );
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  return (
    <Root>
      <CssBaseline />
      <PageTitle />
      <ThemeProvider theme={theme}>
        <Container>
          <PostHogConsentManager />
          <CookieConsentBanner />
          <ConfigureFlatpickrLocale>
            {!isLargeScreen ? <DrawerMenu /> : null}
            <Snackbar />
          </ConfigureFlatpickrLocale>
          {isLargeScreen ? (
            <nav className={classes.drawer}>
              <DrawerMenu variant="permanent" forceOpen />
            </nav>
          ) : null}
          <Content>
            <Outlet />
          </Content>
        </Container>
      </ThemeProvider>
      {!import.meta.env.VITE_E2E && <TanStackRouterDevtools />}
    </Root>
  );
};

export const Route = createRootRoute({
  component: RootLayout,
});
